import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { Plus, Edit, Trash2, Eye, EyeOff, Tag } from 'lucide-react';
import { toast } from 'sonner';

interface PromoCode {
  id: string;
  code: string;
  description: string | null;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  max_redemptions: number | null;
  usage_count: number;
  per_user_limit: number | null;
  valid_from: string | null;
  valid_until: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export default function AdminPromoCodes() {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState<PromoCode | null>(null);
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage' as 'percentage' | 'fixed_amount',
    discount_value: 0,
    max_redemptions: null as number | null,
    per_user_limit: null as number | null,
    valid_from: '',
    valid_until: '',
    active: true,
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching promo codes...');
      
      const { data, error } = await supabase
        .from('promo_codes')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('📊 Promo codes query result:', { data, error });

      if (error) {
        console.error('❌ Error from Supabase:', error);
        throw error;
      }

      if (data) {
        console.log(`✅ Found ${data.length} promo codes`);
        setPromoCodes((data as PromoCode[]) || []);
      } else {
        console.warn('⚠️ No data returned from query');
        setPromoCodes([]);
      }
    } catch (error: any) {
      console.error('❌ Error fetching promo codes:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        details: error.details,
        hint: error.hint,
      });
      
      // Show more detailed error message
      const errorMessage = error.message || 'Failed to load promo codes';
      if (error.code === '42501' || error.message?.includes('permission') || error.message?.includes('RLS')) {
        toast.error('Permission denied: Check RLS policies for promo_codes table');
      } else {
        toast.error(`Failed to load promo codes: ${errorMessage}`);
      }
      setPromoCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNewPromoCode = () => {
    setEditingPromoCode(null);
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 0,
      max_redemptions: null,
      per_user_limit: null,
      valid_from: '',
      valid_until: '',
      active: true,
    });
    setIsDialogOpen(true);
  };

  const handleEditPromoCode = (promoCode: PromoCode) => {
    setEditingPromoCode(promoCode);
    setFormData({
      code: promoCode.code,
      description: promoCode.description || '',
      discount_type: promoCode.discount_type,
      discount_value: promoCode.discount_value,
      max_redemptions: promoCode.max_redemptions,
      per_user_limit: promoCode.per_user_limit,
      valid_from: promoCode.valid_from ? promoCode.valid_from.split('T')[0] : '',
      valid_until: promoCode.valid_until ? promoCode.valid_until.split('T')[0] : '',
      active: promoCode.active,
    });
    setIsDialogOpen(true);
  };

  const handleToggleActive = async (promoCode: PromoCode) => {
    try {
      const { error } = await supabase
        .from('promo_codes')
        .update({ active: !promoCode.active })
        .eq('id', promoCode.id);

      if (error) throw error;

      toast.success(`Promo code ${promoCode.active ? 'deactivated' : 'activated'} successfully`);
      fetchPromoCodes();
    } catch (error: any) {
      console.error('Error toggling promo code:', error);
      toast.error('Failed to update promo code');
    }
  };

  const handleDeletePromoCode = async (promoCode: PromoCode) => {
    if (!confirm(`Are you sure you want to delete promo code "${promoCode.code}"? This action cannot be undone.`)) {
      return;
    }

    try {
      // Check if promo code is being used by any bookings
      const { data: bookings, error: bookingsError } = await supabase
        .from('bookings')
        .select('id')
        .eq('promo_code_id', promoCode.id)
        .limit(1);

      if (bookingsError) {
        console.error('Error checking bookings:', bookingsError);
      }

      if (bookings && bookings.length > 0) {
        toast.error('Cannot delete promo code: It is being used by existing bookings. Deactivate it instead.');
        return;
      }

      const { error } = await supabase
        .from('promo_codes')
        .delete()
        .eq('id', promoCode.id);

      if (error) throw error;

      toast.success('Promo code deleted successfully');
      fetchPromoCodes();
    } catch (error: any) {
      console.error('Error deleting promo code:', error);
      toast.error(error.message || 'Failed to delete promo code');
    }
  };

  const handleSavePromoCode = async () => {
    try {
      // Validation
      if (!formData.code.trim()) {
        toast.error('Code is required');
        return;
      }

      if (formData.discount_value <= 0) {
        toast.error('Discount value must be greater than 0');
        return;
      }

      if (formData.discount_type === 'percentage' && formData.discount_value > 100) {
        toast.error('Percentage discount cannot exceed 100%');
        return;
      }

      // Validate dates
      let validFromDate: string | null = null;
      let validUntilDate: string | null = null;

      if (formData.valid_from) {
        validFromDate = new Date(formData.valid_from + 'T00:00:00Z').toISOString();
      }

      if (formData.valid_until) {
        validUntilDate = new Date(formData.valid_until + 'T23:59:59Z').toISOString();
      }

      if (validFromDate && validUntilDate && validFromDate > validUntilDate) {
        toast.error('Valid until date must be after valid from date');
        return;
      }

      const promoCodeData = {
        code: formData.code.trim().toUpperCase(),
        description: formData.description.trim() || null,
        discount_type: formData.discount_type,
        discount_value: formData.discount_value,
        max_redemptions: formData.max_redemptions || null,
        per_user_limit: formData.per_user_limit || null,
        valid_from: validFromDate,
        valid_until: validUntilDate,
        active: formData.active,
      };

      if (editingPromoCode) {
        // Update existing promo code
        const { error } = await supabase
          .from('promo_codes')
          .update(promoCodeData)
          .eq('id', editingPromoCode.id);

        if (error) throw error;
        toast.success('Promo code updated successfully');
      } else {
        // Create new promo code
        const { error } = await supabase
          .from('promo_codes')
          .insert([promoCodeData]);

        if (error) {
          if (error.code === '23505') {
            toast.error('A promo code with this code already exists');
          } else {
            throw error;
          }
          return;
        }
        toast.success('Promo code created successfully');
      }

      setIsDialogOpen(false);
      fetchPromoCodes();
    } catch (error: any) {
      console.error('Error saving promo code:', error);
      toast.error(error.message || 'Failed to save promo code');
    }
  };

  const formatDiscount = (promoCode: PromoCode) => {
    if (promoCode.discount_type === 'percentage') {
      return `${promoCode.discount_value}%`;
    }
    return `$${promoCode.discount_value.toFixed(2)}`;
  };

  const isExpired = (promoCode: PromoCode) => {
    if (!promoCode.valid_until) return false;
    return new Date(promoCode.valid_until) < new Date();
  };

  const isNotYetValid = (promoCode: PromoCode) => {
    if (!promoCode.valid_from) return false;
    return new Date(promoCode.valid_from) > new Date();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Promo Codes</h2>
          <p className="text-muted-foreground">
            Manage discount codes and promotions
          </p>
        </div>
        <Button onClick={handleNewPromoCode}>
          <Plus className="h-4 w-4 mr-2" />
          New Promo Code
        </Button>
      </div>

      {loading ? (
        <Card className="p-6">
          <p className="text-center text-muted-foreground">Loading promo codes...</p>
        </Card>
      ) : promoCodes.length === 0 ? (
        <Card className="p-6">
          <p className="text-center text-muted-foreground">No promo codes found</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {promoCodes.map((promoCode) => (
            <Card key={promoCode.id} className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg font-mono">{promoCode.code}</h3>
                    <Badge variant={promoCode.active ? 'default' : 'secondary'}>
                      {promoCode.active ? 'Active' : 'Inactive'}
                    </Badge>
                    {isExpired(promoCode) && (
                      <Badge variant="destructive">Expired</Badge>
                    )}
                    {isNotYetValid(promoCode) && (
                      <Badge variant="outline">Not Yet Valid</Badge>
                    )}
                    <Badge variant="outline" className="font-normal">
                      {formatDiscount(promoCode)} off
                    </Badge>
                  </div>
                  {promoCode.description && (
                    <p className="text-sm text-muted-foreground mb-3">
                      {promoCode.description}
                    </p>
                  )}
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground">Type:</span>
                      <span className="font-medium capitalize">{promoCode.discount_type.replace('_', ' ')}</span>
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="text-muted-foreground">Usage:</span>
                      <span className="font-medium">{promoCode.usage_count}</span>
                      {promoCode.max_redemptions && (
                        <span className="text-muted-foreground">/ {promoCode.max_redemptions}</span>
                      )}
                    </span>
                    {promoCode.per_user_limit && (
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground">Per User Limit:</span>
                        <span className="font-medium">{promoCode.per_user_limit}</span>
                      </span>
                    )}
                    {promoCode.valid_from && (
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground">Valid From:</span>
                        <span className="font-medium">
                          {new Date(promoCode.valid_from).toLocaleDateString()}
                        </span>
                      </span>
                    )}
                    {promoCode.valid_until && (
                      <span className="flex items-center gap-1">
                        <span className="text-muted-foreground">Valid Until:</span>
                        <span className="font-medium">
                          {new Date(promoCode.valid_until).toLocaleDateString()}
                        </span>
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  <Button size="sm" variant="outline" onClick={() => handleEditPromoCode(promoCode)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleToggleActive(promoCode)}>
                    {promoCode.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => handleDeletePromoCode(promoCode)}
                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Promo Code Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPromoCode ? 'Edit Promo Code' : 'Create New Promo Code'}</DialogTitle>
            <DialogDescription>
              {editingPromoCode ? 'Update promo code details' : 'Add a new discount code'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Code */}
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                placeholder="SAVE50"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                disabled={!!editingPromoCode}
                className="font-mono"
              />
              <p className="text-xs text-muted-foreground">
                Code will be automatically converted to uppercase
              </p>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="50% off on any booking"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>

            {/* Discount Type and Value */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discount_type">Discount Type *</Label>
                <select
                  id="discount_type"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'percentage' | 'fixed_amount' })}
                >
                  <option value="percentage">Percentage</option>
                  <option value="fixed_amount">Fixed Amount</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="discount_value">
                  Discount Value * 
                  {formData.discount_type === 'percentage' ? ' (%)' : ' ($)'}
                </Label>
                <Input
                  id="discount_value"
                  type="number"
                  min="0"
                  max={formData.discount_type === 'percentage' ? 100 : undefined}
                  step={formData.discount_type === 'percentage' ? '0.01' : '0.01'}
                  placeholder={formData.discount_type === 'percentage' ? '50.00' : '20.00'}
                  value={formData.discount_value}
                  onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="max_redemptions">Max Redemptions</Label>
                <Input
                  id="max_redemptions"
                  type="number"
                  min="1"
                  placeholder="1000 (leave empty for unlimited)"
                  value={formData.max_redemptions || ''}
                  onChange={(e) => setFormData({ ...formData, max_redemptions: e.target.value ? parseInt(e.target.value) : null })}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for unlimited redemptions
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="per_user_limit">Per User Limit</Label>
                <Input
                  id="per_user_limit"
                  type="number"
                  min="1"
                  placeholder="1 (leave empty for unlimited)"
                  value={formData.per_user_limit || ''}
                  onChange={(e) => setFormData({ ...formData, per_user_limit: e.target.value ? parseInt(e.target.value) : null })}
                />
                <p className="text-xs text-muted-foreground">
                  Maximum times a single user can use this code
                </p>
              </div>
            </div>

            {/* Validity Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valid_from">Valid From</Label>
                <Input
                  id="valid_from"
                  type="date"
                  value={formData.valid_from}
                  onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for immediate activation
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="valid_until">Valid Until</Label>
                <Input
                  id="valid_until"
                  type="date"
                  value={formData.valid_until}
                  onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for no expiration
                </p>
              </div>
            </div>

            {/* Active Status */}
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="active" className="text-sm font-normal cursor-pointer">
                Active (promo code is available for use)
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSavePromoCode}>
              {editingPromoCode ? 'Save Changes' : 'Create Promo Code'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

